import { Box } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../common/hooks';
import { getGoalPriorityProbability } from '../../../common/helper';
import { Column, SectionTitle } from '../../../themes/styles/plan-overview';
import { ProbabilityOfSuccessGauge } from '../../molecules/ProbabilityOfSuccessGauge/ProbabilityOfSuccessGauge';
import { iwsGoalTypeMapper } from '../../../common/constants';

interface ProbabilityOfSuccessSectionProps {
    selectedGoalIndex: number;
}

export const ProbabilityOfSuccessSection = ({ selectedGoalIndex }: ProbabilityOfSuccessSectionProps) => {
    const { t } = useTranslation();
    const adviceGoals = useAppSelector((state) => (state as any).adviceGoals);
    const global = useAppSelector((state) => (state as any).global);
    const tenant = useAppSelector((state) => state.auth?.user?.tenant || (state as any).global?.globalConfig?.subdomain);

    const formField = useMemo(() => {
        return adviceGoals?.formFields?.[selectedGoalIndex];
    }, [adviceGoals?.formFields, selectedGoalIndex]);
    const goalId = formField?.goalId;
    // Get the selected goal data
    const goalsData = formField?.data;
    // Get goal response data
    const goalResp = useMemo(() => {
        return adviceGoals?.response?.find((item: any) => item?.goalId === goalId);
    }, [adviceGoals?.response, goalId]);
    // Get goal response data
    const goalCalcResp = useMemo(() => {
        return adviceGoals?.global?.responseGoalCalculator?.find((item: any) => item?.goalId === goalId);
    }, [adviceGoals?.global?.responseGoalCalculator, goalId]);
    // Get goal request data
    const goalReq = useMemo(() => {
        return adviceGoals?.request?.find((item: any) => item?.goalId === goalId);
    }, [adviceGoals?.request, goalId]);

    const goalType = goalsData?.goalType || goalsData?.wealthGoalType;
    const isRetirement = goalType === iwsGoalTypeMapper.retirement || goalsData?.type === iwsGoalTypeMapper.retirement;
    const isIncome = goalsData?.type === iwsGoalTypeMapper.income;
    const goalAmtRetirement = (arr: any, accounts: any) => {
        let min = 0;
        for (let i of arr) {
            if (i < 0) {
                min = i;
                break;
            }
        }
        // We ignore Accounts and probableGoalAmt is 0, ref: AEX-1793
        let retirementIncome = 0;
        accounts?.forEach((acc: any) => {
            if (acc?.fundingType === 'incomeSources') {
                if (acc?.frequency === 'monthly') retirementIncome += 12 * acc?.recurringContribution;
                else retirementIncome += acc?.recurringContribution; //its amount from income sources
            }
        });
        return -1 * min + retirementIncome;
    };

    // Get GOE config
    const goeConfig = global?.goeConfig || {};

    // Calculate goal probabilities and values
    const analysisReport = goalResp?.response?.body?.analysisReport;
    const goalPriorityValue = goeConfig?.goalPriorityProbabilities || {};
    const infusionArray = goalCalcResp?.['response']?.['body']?.['infusions'] || [];

    const currentGoalProbability = Math.round(Number(analysisReport?.currentGoalProbability || 0) * 100);
    const goalPriorityProbability = getGoalPriorityProbability(goalPriorityValue, goalsData?.['goalPriority']) * 100;
    const probableGoalAmount =
        isRetirement || isIncome
            ? goalAmtRetirement(infusionArray, goalReq?.accounts)
            : goalCalcResp?.response?.body?.goalAmt;
    const goalAmount = isRetirement ? goalsData?.['targetedRetirementIncome'] : goalsData?.['goalAmount'];
    // If no goal data available, don't render
    if (!goalsData || !goalResp) {
        return null;
    }

    return (
        <>
            <Column>
                <Box>
                    <SectionTitle>{t('PROBABILITY_OF_SUCCESS')}</SectionTitle>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            marginTop: '24px'
                        }}
                    >
                        <ProbabilityOfSuccessGauge
                            goalType={goalsData.goalPriority}
                            target={goalPriorityProbability}
                            thresholdValue={goalPriorityProbability}
                            value={currentGoalProbability}
                            targetGoalAmount={goalAmount}
                            probableGoalAmount={probableGoalAmount}
                            isRetirement={isRetirement || isIncome}
                            isMonthlyIncome
                            tenant={tenant}
                        />
                    </Box>
                </Box>
            </Column>
        </>
    );
};
